import { useCallback, useEffect, useRef, useState } from 'react'
import { metaSetter } from '@app/utils'
import { _ONBOARDED } from '@app/lib/cookies/names'
import { useInteractiveContext } from '@app/components/providers/interactive'
import { ModalType } from '@app/data/enums'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { getDashboardBaseStyle } from '@app/styles/dashboard-styles'
import { QueryView } from '@app/components/audit/query-view'
import { DashboardWebsiteListMock } from '@app/components/general/mocks/dashboard-websites-list-mock'
import { websitesMock } from '@app/data/mocks/websites'
import { ViewConfigTitleMock } from '@app/components/general/mocks/dashboard-view-config-mock'
import { useWasmContext } from '@app/components/providers'
import { SortableWebsiteListMock } from '@app/components/general/mocks/dashboard-sortable-mock'
import { RightBarMock } from '@app/components/general/mocks/dashboard-rightbar-mock'
import { DashboardDrawerMock } from '@app/components/general/drawers/dashboard-drawer-mock'
import { AppLayout } from '@app/components/app-layout'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { WebsiteProviderComponentMock } from '@app/components/providers/website-mock'
import { useMountEffectOnce } from '@app/lib/hooks/use-mount-effect-once'
import { useToast } from '@app/@/components/ui/use-toast'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/demo',
    }),
  }
}

// mock default account data
const account = {
  activeSubscription: true,
  authed: false,
  alertEnabled: true,
  inited: true,
  analyticsDisabled: false,
}

// translated issues done prior.
const validLocales = ['ja', 'de']

// load the mock data locale
const loadMockData = async (local?: string) => {
  let rhinobinoMock = null
  let elegantfishermanMock = null

  // handle all locales lazily and Parrallel
  try {
    const hasLocale = local && validLocales.includes(local)
    const dataMocks = await Promise.all([
      import(`@app/data/mocks/rhinobino-mock${hasLocale ? `-${local}` : ''}`),
      import(
        `@app/data/mocks/elegantfisherman-mock${hasLocale ? `-${local}` : ''}`
      ),
    ])

    if (dataMocks && dataMocks.length >= 1) {
      rhinobinoMock = dataMocks[0].rhinobinoMock
    }
    if (dataMocks && dataMocks.length >= 2) {
      elegantfishermanMock = dataMocks[1].elegantfishermanMock
    }
  } catch (e) {
    console.error(e)
  }

  return {
    rhinobinoMock: rhinobinoMock || [],
    elegantfishermanMock: elegantfishermanMock || [],
  }
}

// todo: DEMO DATA
function Demo() {
  const { t, lang } = useTranslation('demo')
  const { feed } = useWasmContext()

  // top level state
  const [sortModalVisible, setSortModalVisible] = useState<boolean>(false)
  const [queryModalVisible, setQueryModalVisible] = useState<boolean>(false)
  const [standard, setStandard] = useState<string>('WCAG2AA')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({
    elegantfishermanMock: [],
    rhinobinoMock: [],
  })

  const { toast } = useToast()
  const [_, forceUpdate] = useState(0)

  const { setModal, selectedWebsite } = useInteractiveContext()

  const websitedata = useRef(websitesMock)

  useMountEffectOnce(() => {
    ;(async () => {
      setLoading(true)
      const { elegantfishermanMock, rhinobinoMock } = await loadMockData(lang)

      setData({
        elegantfishermanMock,
        rhinobinoMock,
      })

      setLoading(false)
    })()
  })

  useEffect(() => {
    for (const item of data.rhinobinoMock) {
      feed.insert_website(item)
    }

    for (const item of data.elegantfishermanMock) {
      feed.insert_website(item)
    }
  }, [feed, data])

  const onWebsiteSort = useCallback(
    () =>
      setSortModalVisible((v) => {
        if (queryModalVisible) {
          setQueryModalVisible(false)
        }
        return !v
      }),
    [setSortModalVisible, setQueryModalVisible, queryModalVisible]
  )

  const onQueryEvent = useCallback(
    () =>
      setQueryModalVisible((v) => {
        if (sortModalVisible) {
          setSortModalVisible(false)
        }
        return !v
      }),
    [setQueryModalVisible, sortModalVisible, setSortModalVisible]
  )

  const onAnalyticsEvent = () => {
    setModal({
      open: true,
      url: '',
      modalType: ModalType.analytics,
      data: websitesMock,
    })
  }

  const onStandardChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setStandard(e.target.value)

  const { sortStyle, queryStyle } = getDashboardBaseStyle({
    sortModalVisible,
    queryModalVisible,
  })

  const onSubmitSort = async (sortOrder: { id: string }[]) => {
    const nextItems = []

    for (const index in sortOrder) {
      const item = sortOrder[index]
      const findItem = websitesMock.findIndex((d) => d && d.domain === item.id)

      const dataSet = websitesMock[findItem]

      if (dataSet) {
        nextItems.push(dataSet)
      }
    }
    // todo: use fast replacing
    websitedata.current = nextItems

    forceUpdate((x) => x + 1)
  }

  const scanall = async () => {
    toast({
      title: t('crawl-starting'),
    })
  }

  const currentWebsites = websitedata.current

  return (
    <WebsiteProviderComponentMock skip>
      <DashboardDrawerMock demo>
        <div className='pb-20'>
          <h1 className='sr-only'>{t('header')}</h1>
          <ViewConfigTitleMock
            title={t('all-sites')}
            data={currentWebsites as any}
          >
            <RightBarMock
              premiumEnabled={account?.activeSubscription}
              authenticated={account?.authed}
              sortModalVisible={sortModalVisible}
              queryModalVisible={queryModalVisible}
              onQueryEvent={onQueryEvent}
              onWebsiteSort={onWebsiteSort}
              onScanAllEvent={scanall}
              onAnalyticsEvent={onAnalyticsEvent}
              translation={'demo'}
              dialogDisabled
            />
          </ViewConfigTitleMock>

          <div className={sortStyle}>
            <SortableWebsiteListMock
              data={currentWebsites}
              visible={sortModalVisible}
              onSubmitSort={onSubmitSort}
            />
          </div>

          <QueryView
            queryStyle={queryStyle}
            onStandardChange={onStandardChange}
            standard={standard}
            queryModalVisible={queryModalVisible}
            t={t}
          />

          <DashboardWebsiteListMock
            queryModalVisible={queryModalVisible}
            sortModalVisible={sortModalVisible}
            translation='demo'
            websitesMock={
              selectedWebsite
                ? currentWebsites.filter((item) => item.url === selectedWebsite)
                : currentWebsites
            }
            loading={!!loading}
          />
        </div>
      </DashboardDrawerMock>
    </WebsiteProviderComponentMock>
  )
}

Demo.getLayout = AppLayout

export default metaSetter(
  { Demo },
  {
    wasm: true,
    translation: 'demo',
    gql: true,
  }
)
